import { useState } from 'react';
import { Card, Tag, Table } from 'antd';
import { CloseSquareFilled } from '@ant-design/icons';
import { v4 as uuid } from 'uuid';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import ContentHeader from '../../components/ContentHeader';
import { PlacesAutoComplete } from '../../components/forms';
import { showErrorToast } from '../../services/notificationsService';
import { postRequest } from '../../services/requestService';
import { Spinner } from '../../components/Spinner';

import './index.scss';
import { getTableColumns } from './constants';

const UnmaskingAddressPage = () => {
  useDocumentTitle('Insurance POC');
  const [response, setResponse] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchBuyers = async (selectedAddress) => {
    setIsLoading(true);
    setHasSearched(true);
    setResponse({});
    try {
      const apiResponse = await postRequest('/data/insurance/unmask', {
        address: selectedAddress,
      });
      setResponse(apiResponse?.data?.data || {});
    } catch {
      showErrorToast('Fetching data failed!');
    }
    setIsLoading(false);
  };

  const onPlacesChange = async (newAddress) => {
    if (!newAddress) return;
    await fetchBuyers(newAddress);
  };

  return (
    <>
      <ContentHeader title="Insurance POC" />
      <div className="unmasking-address-page">
        <div className="search-container">
          <PlacesAutoComplete
            title={null}
            disabled={isLoading}
            allowFullAddress
            placeholder="3723 Temple St, Tampa, FL 33619"
            onChange={(value) => onPlacesChange(value)}
            clearInputOnSelect={false}
            clearInputOnBlur={false}
            allowClear={{ clearIcon: <CloseSquareFilled /> }}
          />
        </div>
        {!hasSearched && (
          <span style={{ color: '#3F434A' }}>
            {`Type a property address and we'll show you all the other assets owned by the owner of this property.`}
          </span>
        )}
        {isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: '25px',
            }}
          >
            <Spinner />
            <span>Searching ...</span>
          </div>
        )}
        {!isLoading && hasSearched && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '10px',
              marginTop: '25px',
            }}
          >
            <Card
              title={
                <>
                  Property information
                  <Tag color="#3F434A" style={{ marginLeft: '5px' }}>
                    {response?.isLLC ? 'LLC' : 'INDIVIDUAL'}
                  </Tag>
                </>
              }
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '5px',
                }}
              >
                <span>
                  {`Address: ${response?.property?.address}, ${response?.property?.city}, ${response?.property?.state} ${response?.property?.zip}`}
                </span>
                <span>
                  {`Owners: ${
                    [
                      response?.property?.owner_name_1,
                      response?.property?.owner_name_2,
                      response?.property?.owner_name_3,
                      response?.property?.owner_name_4,
                      response?.property?.owner_name_5,
                    ]
                      .filter(Boolean)
                      .join(' | ') || 'N/D'
                  }`}
                </span>
                <span>{`Beds: ${response?.property?.bed_count || 'N/D'}`}</span>
                <span>{`Baths: ${
                  response?.property?.bath_count || 'N/D'
                }`}</span>
                <span>{`Year built: ${
                  response?.property?.year_built || 'N/D'
                }`}</span>
              </div>
            </Card>
            <Card title="Owned entities research">
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '10px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '5px',
                  }}
                >
                  <h3>Open corporates:</h3>
                  {(response?.openCorporates || []).map((item) => (
                    <span key={uuid()}>{item}</span>
                  ))}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '5px',
                  }}
                >
                  <h3>Found in the internet:</h3>
                  {response?.gptOutput && response.gptOutput.length > 0 ? (
                    response.gptOutput.map((llc) => (
                      <span key={uuid()}>{llc.entity}</span>
                    ))
                  ) : (
                    <span>N/D</span>
                  )}
                </div>
              </div>
            </Card>
            <Card title="Tax Assessor Data">
              <Table
                dataSource={response?.properties || []}
                columns={getTableColumns()}
              />
            </Card>
          </div>
        )}
      </div>
    </>
  );
};

export default UnmaskingAddressPage;
